/**
 * Vault Nudges module — split out of pages/Vault.jsx (2026-02).
 *
 * Self-contained: NudgesPanel (list + create) + GmailConnectionStrip
 * (Google delegation status/connect) + AutomatedRemindersStrip
 * (60/30-day license + quarterly CAQH sweep) + RemindersColumn +
 * NudgeCreate (create-invite dialog). Zero behaviour change from the
 * pre-split inline definitions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtDate } from '../../lib/date';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Mail, Plus, RefreshCw, Ban, Copy, CheckCircle, AlarmClock, ExternalLink,
} from 'lucide-react';

// ═════════════════ Nudges (kept from iter 113) ═════════════════

function NudgesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/nudges');
      setRows(data.nudges || []);
    } catch { toast.error('Could not load nudges'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyLink = async (url, recipient) => {
    if (!url) { toast.error('No link available for this nudge'); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = url; ta.setAttribute('readonly', '');
        ta.style.position = 'absolute'; ta.style.left = '-9999px';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast.success(recipient ? `Link copied — send to ${recipient}` : 'Link copied to clipboard');
    } catch {
      toast.error('Could not copy — link: ' + url);
    }
  };

  const send = async (payload) => {
    try {
      const { data } = await api.post('/vault/nudges', payload);
      const delivered = data?.email_delivery?.ok;
      if (delivered) {
        toast.success('Nudge sent — email delivered');
      } else {
        // Auto-copy so the admin can hand-deliver when email fails
        await copyLink(data?.onboard_url, data?.recipient_email);
        toast.warning('Email delivery failed — link copied to clipboard so you can send it manually', { duration: 8000 });
      }
      setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const resend = async (id) => {
    try {
      const { data } = await api.post(`/vault/nudges/${id}/resend`);
      const delivered = data?.email_delivery?.ok;
      if (delivered) {
        toast.success('Nudge resent — email delivered');
      } else {
        await copyLink(data?.onboard_url, data?.recipient_email);
        toast.warning('Resend email failed — link copied to clipboard', { duration: 8000 });
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this onboarding link?')) return;
    try { await api.post(`/vault/nudges/${id}/revoke`); toast.success('Revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <GmailConnectionStrip />
      <AutomatedRemindersStrip />
      <Card className="p-4" data-testid="vault-nudges">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-slate-600">{rows.length} nudges</div>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="nudge-add"><Plus size={14} className="mr-1" /> Send Nudge</Button>
      </div>
      {loading ? <div className="p-6 text-slate-500">Loading…</div> : (
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <tr>
              <th className="py-2 pr-3">Recipient</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Sent</th>
              <th className="py-2 pr-3">Expires</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(n => (
              <tr key={n.nudge_id} className="border-b" data-testid={`nudge-row-${n.nudge_id}`}>
                <td className="py-2 pr-3"><div className="font-medium">{n.recipient_name || '—'}</div><div className="text-xs text-slate-500">{n.recipient_email}</div></td>
                <td className="py-2 pr-3 text-xs">{n.status}</td>
                <td className="py-2 pr-3 text-xs">{fmtDate(n.sent_at)}</td>
                <td className="py-2 pr-3 text-xs">{fmtDate(n.expires_at)}</td>
                <td className="py-2 text-right">
                  {n.status !== 'submitted' && n.status !== 'revoked' && n.status !== 'expired' && (
                    <>
                      {n.onboard_url && (
                        <Button size="sm" variant="ghost" onClick={() => copyLink(n.onboard_url, n.recipient_email)} data-testid={`nudge-copy-${n.nudge_id}`}><Copy size={12} className="mr-1" /> Copy link</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => resend(n.nudge_id)} data-testid={`nudge-resend-${n.nudge_id}`}><RefreshCw size={12} className="mr-1" /> Resend</Button>
                      <Button size="sm" variant="ghost" onClick={() => revoke(n.nudge_id)} data-testid={`nudge-revoke-${n.nudge_id}`}><Ban size={12} className="mr-1" /> Revoke</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {creating && <NudgeCreate onClose={() => setCreating(false)} onSend={send} />}
    </Card>
    </div>
  );
}

function GmailConnectionStrip() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/gmail/status');
      setStatus(data);
    } catch { setStatus({ connected: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle the ?gmail_connected=1|error redirect from the OAuth callback.
  useEffect(() => {
    const url = new URL(window.location.href);
    const g = url.searchParams.get('gmail_connected');
    if (!g) return;
    if (g === '1') {
      const sender = url.searchParams.get('sender') || 'your mailbox';
      toast.success(`Gmail connected — sending as ${sender}`);
    } else {
      const reason = url.searchParams.get('reason') || 'unknown_error';
      const detail = url.searchParams.get('detail') || '';
      toast.error(`Gmail connection failed: ${reason}${detail ? ` (${detail})` : ''}`, { duration: 10000 });
    }
    url.searchParams.delete('gmail_connected');
    url.searchParams.delete('sender');
    url.searchParams.delete('reason');
    url.searchParams.delete('detail');
    window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/admin/gmail/connect');
      if (data?.authorization_url) window.location.href = data.authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Google sign-in'); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Gmail? Nudges + reminders will stop sending until you reconnect.')) return;
    try { await api.post('/admin/gmail/disconnect'); toast.success('Gmail disconnected'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const testSend = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/admin/gmail/test');
      if (data?.ok) toast.success('Test email sent — check your inbox + Sent folder');
      else toast.error(`Send failed: ${data?.reason || 'unknown'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); }
    finally { setTesting(false); }
  };

  const connected = !!status?.connected;
  return (
    <Card className="p-4" data-testid="vault-gmail-strip" style={{ borderLeft: `4px solid ${connected ? '#839E97' : '#B08A3A'}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Mail size={16} className={connected ? 'text-[#839E97]' : 'text-[#B08A3A]'} />
            <span className="font-serif text-lg text-[#2D2833]">Outbound email</span>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${connected ? 'bg-[rgba(131,158,151,0.18)] text-[#5A7770]' : 'bg-[rgba(176,138,58,0.18)] text-[#7A5F1F]'}`} data-testid="gmail-status-badge">
              {connected ? 'Gmail connected' : 'Not connected'}
            </span>
          </div>
          <div className="text-xs text-[#685B72] mt-1.5 max-w-2xl leading-relaxed">
            {connected ? (
              <>Sending as <b className="text-[#2D2833]">{status?.sender_email}</b> · Nudges, CAQH sweeps, license reminders, feedback receipts and referral confirmations all use this mailbox. Every send lands in that mailbox's Sent folder. Zero per-email cost — uses your Workspace quota (2,000/day).</>
            ) : (
              <>Emails aren't sending right now. Connect a @thrivespeech.net Gmail account so nudges + reminders deliver from that mailbox and appear in its Sent folder.</>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          {connected ? (
            <>
              <Button size="sm" variant="outline" onClick={testSend} disabled={testing} data-testid="gmail-test-send">
                {testing ? 'Sending…' : 'Send test email'}
              </Button>
              <Button size="sm" variant="outline" onClick={disconnect} data-testid="gmail-disconnect">Disconnect</Button>
            </>
          ) : (
            <Button size="sm" onClick={connect} disabled={busy} data-testid="gmail-connect">
              {busy ? 'Redirecting…' : 'Connect Gmail'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function AutomatedRemindersStrip() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/reminders/log', { params: { limit: 20 } });
      setRows(data.reminders || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/vault/reminders/run/${kind}`);
      const s = data.stats || {};
      toast.success(`${kind === 'license' ? 'License' : 'CAQH'} sweep: ${s.sent || 0} sent, ${s.skipped_dup || s.skipped_recent || 0} skipped, ${s.failed || 0} failed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sweep failed'); }
    finally { setBusy(null); }
  };

  const licenseRows = rows.filter(r => r.kind === 'license_expiry').slice(0, 3);
  const caqhRows = rows.filter(r => r.kind === 'caqh_quarterly').slice(0, 3);

  return (
    <Card className="p-4" data-testid="vault-reminders-strip">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg text-[#2D2833]">Automated reminders</div>
          <div className="text-xs text-[#685B72] mt-0.5 max-w-xl leading-relaxed">
            Providers get an email 60 days and again 30 days before their license expires. Office Directors get a CAQH refresh reminder every 90 days. Both jobs run daily at 07:00 CT — you can also trigger a sweep manually below.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => run('license')} disabled={busy === 'license'} data-testid="reminders-run-license">
            {busy === 'license' ? 'Running…' : 'Run license sweep'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => run('caqh')} disabled={busy === 'caqh'} data-testid="reminders-run-caqh">
            {busy === 'caqh' ? 'Running…' : 'Run CAQH sweep'}
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400">Loading recent sends…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <RemindersColumn title="Recent license reminders" rows={licenseRows} emptyText="No license reminders have fired yet." tidPrefix="reminders-license" />
          <RemindersColumn title="Recent CAQH reminders" rows={caqhRows} emptyText="No CAQH reminders have fired yet." tidPrefix="reminders-caqh" />
        </div>
      )}
    </Card>
  );
}

function RemindersColumn({ title, rows, emptyText, tidPrefix }) {
  return (
    <div className="rounded-lg border border-[#E8E5EC] p-3" data-testid={`${tidPrefix}-list`}>
      <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">{emptyText}</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map(r => (
            <div key={r.reminder_id} className="text-xs flex items-center justify-between gap-2" data-testid={`${tidPrefix}-row`}>
              <div className="truncate">
                <span className="font-mono text-[10px] text-[#685B72]">{fmtDate(r.sent_at)}</span>
                <span className="mx-1.5 text-slate-300">·</span>
                <span className="text-[#2D2833]">{r.subject_email}</span>
                {r.metadata?.milestone && <span className="ml-1.5 text-slate-500">({r.metadata.milestone}d)</span>}
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.delivery_ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {r.delivery_ok ? 'sent' : 'failed'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NudgeCreate({ onClose, onSend }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send credentialing nudge</DialogTitle>
          <DialogDescription>They'll get a secure link that walks them through updating their CAQH profile (adding Thrive as a practice location) and filling in their full credentialing form. Link expires in 14 days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Recipient Name" v={name} onChange={setName} tid="nudge-name" />
          <Field label="Recipient Email" v={email} onChange={setEmail} tid="nudge-email" />
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notes (internal)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="nudge-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSend({ recipient_email: email, recipient_name: name, notes })} data-testid="nudge-send">Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════ Audit Log (super_user only) ═══════════════════

function Field({ label, v, onChange, tid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <Input value={v || ''} onChange={e => onChange(e.target.value)} data-testid={tid} />
    </div>
  );
}

export { NudgesPanel };
